import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import Api from "@services/Api";
import { toastError } from "@utils/functions";
import { Order, OrderStatus } from "@utils/types";
import { confirmAlert } from "react-confirm-alert";
import store from ".";
import Auth from "@services/Auth";
import { reactConfirmInsertNameAndPhone } from "./confirm-alert-components";

const order = createSlice({
  name: "order",
  initialState: {
    loadingOrder: true,
    loadingAddProductRequest: false,
    addProductToOrderSuccess: false,
    loadingRemoveProductFromOrder: false,
    removeProductFromOrderSuccess: false,
    data: null as Order | null,
  },
  reducers: {
    setLoadingOrder(state, action: PayloadAction<boolean>) {
      state.loadingOrder = action.payload;
    },
    setLoadingAddProductRequest(state, action: PayloadAction<boolean>) {
      state.loadingAddProductRequest = action.payload;
    },
    setOrder(state, action: PayloadAction<Order>) {
      state.data = action.payload;
    },
    toggleAddProductToOrderSuccess(state, action: PayloadAction<boolean | undefined>) {
      state.addProductToOrderSuccess = action.payload !== undefined ? action.payload : !state.addProductToOrderSuccess;
    },
  },
  extraReducers: (builder) => {
    /* fetchOrder cases */
    builder.addCase(fetchOrder.fulfilled, (state, action) => {
      state.data = action.payload;
      state.loadingOrder = false;
      state.loadingAddProductRequest = false;
    });

    builder.addCase(fetchOrder.rejected, (state, action) => {
      toastError(action.payload);
      state.loadingOrder = false;
    });

    /* addProductToOrder cases */
    builder.addCase(addProductToOrder.pending, (state, action) => {
      state.loadingAddProductRequest = true;
    });

    builder.addCase(addProductToOrder.fulfilled, (state, action: PayloadAction<Order>) => {
      if (action.payload) {
        state.data = action.payload;
      }
      state.loadingAddProductRequest = false;
      state.addProductToOrderSuccess = true; // Used to activate the success tooltip message
    });

    builder.addCase(addProductToOrder.rejected, (state, action: PayloadAction<any>) => {
      if (action.payload?.message !== "need-confirmation") toastError(action.payload);
      state.loadingAddProductRequest = false;
    });

    /* removeProductFromOrder cases  */
    builder.addCase(removeProductFromOrder.pending, (state, action) => {
      state.loadingRemoveProductFromOrder = true;
    });

    builder.addCase(removeProductFromOrder.fulfilled, (state, action: PayloadAction<Order>) => {
      if (action.payload) {
        state.data = action.payload;
      }
      state.loadingRemoveProductFromOrder = false;
      state.removeProductFromOrderSuccess = true; // Used to activate the success tooltip message
    });

    builder.addCase(removeProductFromOrder.rejected, (state, action: PayloadAction<any>) => {
      if (action.payload?.message !== "need-confirmation") toastError(action.payload);
      state.loadingRemoveProductFromOrder = false;
    });
  },
});

export default order.reducer;

export const { setLoadingOrder, setOrder, toggleAddProductToOrderSuccess } = order.actions;

interface AddProductToOrderPayload {
  force_coupon?: boolean;
  product_id: number;
  variations: {
    id: number;
    quantity?: number;
  }[];
  customer_name?: string;
  customer_phone?: string;
}
export const addProductToOrder = createAsyncThunk("order/addProductToOrder", async (payload: AddProductToOrderPayload, { rejectWithValue, dispatch }) => {
  let handleConfirmRequest;

  const state = store.getState();
  const code = state.order.data?.code;
  try {
    const body = { ...payload, company_id: state.company.data.id } as any;
    if (code) body.order_code = code;
    const sellerId = localStorage.getItem(`company_${state.company.data.code}_seller_id`);
    if (sellerId) body.seller_id = sellerId;
    const { data } = await Api.post(`/order/add_product`, body);
    localStorage.setItem(`company_${state.company.data.code}_order_code`, data.order.code);
    return data.order;
  } catch (error: any) {
    if (error?.response?.data?.message === "need-confirmation") {
      handleConfirmRequest = () => {
        confirmAlert({
          title: "Atenção",
          message: "Tem certeza que deseja alterar este produto da sua sacola? Se sim, o cupom será removido.",
          buttons: [
            {
              label: "Sim",
              onClick: () => {
                dispatch(addProductToOrder({ ...payload, force_coupon: true }));
              },
            },
            {
              label: "Cancelar",
              onClick: () => {},
            },
          ],
        });
      };
      handleConfirmRequest();
      return rejectWithValue(new Error("need-confirmation"));
    }
    if (error?.response?.data?.message === "authorization-required") {
      handleConfirmRequest = () => {
        confirmAlert({
          closeOnClickOutside: false,
          title: "Atenção",
          message: "Para comprar nessa loja é necessário realizar o login.",
          buttons: [
            {
              label: "Ok",
              onClick: () => {
                window.location.href = `/login?redirect=${window.location.pathname}`;
              },
            },
          ],
        });
      };
      handleConfirmRequest();
    }
    if (error?.response?.data?.message === "pre-register-approve-required") {
      handleConfirmRequest = () => {
        const isAuthenticated = Auth.isAuthenticated();
        confirmAlert({
          closeOnClickOutside: false,
          title: "Atenção",
          message: `Para ter acesso aos preços e poder comprar nessa loja,
          é necessário estar cadastrado e que algum vendedor libere seu acesso ao catálogo.
          Tente novamente mais tarde.`,
          buttons: isAuthenticated
            ? [
                {
                  label: "Ok",
                  onClick: () => {},
                },
              ]
            : [
                {
                  label: "Entrar",
                  onClick: () => {
                    window.location.href = `/login?redirect=${window.location.pathname}`;
                  },
                },
                {
                  label: "Cancelar",
                  onClick: () => {},
                },
              ],
        });
      };
      handleConfirmRequest();
    }
    if (error?.response?.data?.message === "name-and-phone-required") {
      handleConfirmRequest = () => {
        confirmAlert(
          reactConfirmInsertNameAndPhone((customer_name: string, customer_phone: string) => dispatch(addProductToOrder({ ...payload, customer_name, customer_phone })))
        );
      };
      handleConfirmRequest();
    }
    console.log(error, "error on addProductToOrder()");
    throw rejectWithValue(error);
  }
});

export const removeProductFromOrder = createAsyncThunk("order/removeProductFromOrder", async (payload: AddProductToOrderPayload, { rejectWithValue, dispatch }) => {
  const handleConfirmRequest = () => {
    confirmAlert({
      title: "Atenção",
      message: "Tem certeza que deseja alterar este produto da sua sacola? Se sim, o cupom será removido.",
      buttons: [
        {
          label: "Sim",
          onClick: () => {
            dispatch(removeProductFromOrder({ ...payload, force_coupon: true }));
          },
        },
        {
          label: "Cancelar",
          onClick: () => {},
        },
      ],
    });
  };
  const state = store.getState();
  const code = state.order.data?.code;
  try {
    const body = { ...payload, company_id: state.company.data.id } as any;
    if (code) body.order_code = code;
    const { data } = await Api.post(`/order/add_product`, body);
    return data.order;
  } catch (error: any) {
    if (error?.response?.data?.message === "need-confirmation") {
      handleConfirmRequest();
      return rejectWithValue(new Error("need-confirmation"));
    }
    console.log(error, "error on removeProductFromOrder()");
    throw rejectWithValue(error);
  }
});

export const fetchOrder = createAsyncThunk("order/fetchOrder", async (payload, { rejectWithValue }) => {
  const state = store.getState();
  const orderCode = localStorage.getItem(`company_${state.company.data.code}_order_code`);
  if (!orderCode) return;
  try {
    const { data } = await Api.get(`/order/code/${orderCode}`);
    if (data.order.status === OrderStatus.CANCELLED) {
      localStorage.removeItem(`company_${state.company.data.code}_order_code`);
      return null;
    }
    return data.order;
  } catch (error) {
    console.log(error, "error on fetchOrder()");
    throw rejectWithValue(error);
  }
});
